import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Button, notification, Tooltip } from 'src/common';
import { Input } from 'antd';
import styles from './index.module.scss';
import { useMutation } from '@apollo/client';
import { RENAME_SEARCH_PROFILE } from '../../queries';
import { ISearchProfile } from '../../types';
import { trackRenameSearchProfile } from 'src/segment/events';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-light-svg-icons';

interface IProps {
  groupId: string;
  groupTitle: string;
}

interface IFormState {
  title: string;
}

export const RenameSearchProfile: FC<IProps> = (props: IProps) => {
  const { groupId, groupTitle } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<Input>(null);

  const [renameSearchProfile] = useMutation<{ updateSearchProfile: ISearchProfile }, { id: string; name: string }>(
    RENAME_SEARCH_PROFILE
  );

  const onCancel = useCallback(() => {
    setEditing(false);
  }, []);

  const onSubmit = useCallback(() => {
    const title = form.getFieldValue('title');
    if (title === groupTitle || !title) {
      onCancel();
      form.setFieldsValue({ title: groupTitle });
      return;
    }
    trackRenameSearchProfile({
      id: groupId,
      name: title,
      prevName: groupTitle
    });
    renameSearchProfile({ variables: { id: groupId, name: title } })
      .then(() => {
        onCancel();
      })
      .catch(() => {
        notification.error({
          description: t('Common.unknownErrorDesc'),
          message: t('Common.unknownError')
        });
      });
  }, [form, groupId, groupTitle, onCancel, renameSearchProfile, t]);

  const initialValues = useMemo((): IFormState => {
    return {
      title: groupTitle
    };
  }, [groupTitle]);

  const toggleEdit = useCallback(() => {
    setEditing(!editing);
    form.setFieldsValue({ title: groupTitle });
  }, [groupTitle, editing, form]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  return (
    <>
      {editing ? (
        <div className={styles.templateForm}>
          <Form initialValues={initialValues} component={false} onFinish={onSubmit} form={form}>
            <Form.Item name="title">
              <Input
                ref={inputRef}
                name="title"
                type="text"
                maxLength={30}
                placeholder={t('Common.name')}
                onPressEnter={onSubmit}
              />
            </Form.Item>
          </Form>
        </div>
      ) : (
        <span className={styles.title}>{groupTitle}</span>
      )}
      <Tooltip title={t('KeywordSearch.renameSearchProfile')}>
        <Button type={'text'} icon={<FontAwesomeIcon icon={faEdit} />} onClick={toggleEdit} />
      </Tooltip>
    </>
  );
};
export default RenameSearchProfile;
